import type { FC } from "react"

import { useHubspotContractsEdit } from "./useHubspotContractsEdit"

import { FragmentType, getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

import styles from "../GeneralInfoTab.module.scss"

export const HubspotContractsSectionFragment = graphql(/* GraphQL */ `
	fragment HubspotContractsSection_HubspotContract on HubspotContract {
		id
		name
		dealstage
		mainAssignee
		registerTime
	}
`)

type HubspotContractsSectionProps = {
	companyId: string
	contracts: readonly FragmentType<typeof HubspotContractsSectionFragment>[]
}

const formatRegisterDate = (registerTime: string): string =>
	new Date(registerTime).toLocaleDateString("ja-JP")

const HubspotContractsSection: FC<HubspotContractsSectionProps> = ({
	companyId,
	contracts: contractFragments,
}) => {
	const contracts = contractFragments.map((c) =>
		getFragmentData(HubspotContractsSectionFragment, c)
	)
	const hubspot = useHubspotContractsEdit(
		companyId,
		contracts.map((c) => c.id)
	)

	const currentCount = contracts.length
	const draftCount = hubspot.draftIds.length
	const showDelta = hubspot.editing && draftCount !== currentCount

	return (
		<div className={styles.formContainer}>
			<div className={styles.formHeader}>
				<h2 className={styles.formTitle}>
					{showDelta
						? `HubSpot 取引 (${currentCount} 件 → ${draftCount} 件)`
						: `HubSpot 取引 (${currentCount} 件)`}
				</h2>
				{hubspot.editing ? (
					<div className={styles.editActions}>
						<button
							type="button"
							className={styles.cancelButton}
							onClick={hubspot.cancelEdit}
							disabled={hubspot.saving}
						>
							キャンセル
						</button>
						<button
							type="button"
							className={styles.editButton}
							onClick={hubspot.save}
							disabled={hubspot.saving}
						>
							{hubspot.saving ? "保存中..." : "保存"}
						</button>
					</div>
				) : (
					<button
						type="button"
						className={styles.editButton}
						onClick={hubspot.startEdit}
					>
						編集
					</button>
				)}
			</div>

			<div className={styles.hubspotContractList}>
				{hubspot.editing ? (
					<>
						<div className={styles.editNotice}>
							保存ボタンを押すと、ここに表示されている ID
							で紐付けが上書き保存されます。
						</div>
						{hubspot.draftIds.length === 0 ? (
							<div className={styles.emptyMessage}>
								紐付けられた HubSpot 取引はありません
							</div>
						) : (
							<table className={styles.hubspotContractTable}>
								<thead>
									<tr>
										<th className={styles.tableHeader}>ID</th>
										<th className={styles.tableHeader}>取引名</th>
										<th className={styles.tableHeader}>ステージ</th>
										<th className={styles.tableHeader}>メイン担当者</th>
										<th className={styles.tableHeader}>登録日時</th>
										<th className={styles.tableHeaderAction}>アクション</th>
									</tr>
								</thead>
								<tbody>
									{hubspot.draftIds.map((id) => {
										const existing = contracts.find((c) => c.id === id)
										return (
											<tr key={id} className={styles.tableRow}>
												<td className={styles.tableCell}>{id}</td>
												<td className={styles.tableCell}>
													{existing?.name ?? "(新規追加)"}
												</td>
												<td className={styles.tableCell}>
													{existing?.dealstage ?? "-"}
												</td>
												<td className={styles.tableCell}>
													{existing?.mainAssignee ?? "-"}
												</td>
												<td className={styles.tableCell}>
													{existing
														? formatRegisterDate(existing.registerTime)
														: "-"}
												</td>
												<td className={styles.tableCellAction}>
													<button
														type="button"
														className={styles.removeButton}
														onClick={() => hubspot.removeId(id)}
														disabled={hubspot.saving}
													>
														削除
													</button>
												</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						)}

						<div className={styles.addRow}>
							<input
								type="text"
								className={styles.addInput}
								placeholder="HubSpot Deal ID（数値）"
								aria-label="HubSpot Deal ID（数値）"
								inputMode="numeric"
								value={hubspot.newId}
								onChange={(e) => hubspot.setNewId(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault()
										hubspot.addId()
									}
								}}
								disabled={hubspot.saving}
							/>
							<button
								type="button"
								className={styles.addButton}
								onClick={hubspot.addId}
								disabled={hubspot.saving || hubspot.newId.trim() === ""}
							>
								追加
							</button>
						</div>

						{hubspot.inlineError && (
							<div className={styles.editError}>{hubspot.inlineError}</div>
						)}
					</>
				) : currentCount === 0 ? (
					<div className={styles.emptyMessage}>
						紐付けられている HubSpot 取引はありません
					</div>
				) : (
					<table className={styles.hubspotContractTable}>
						<thead>
							<tr>
								<th className={styles.tableHeader}>ID</th>
								<th className={styles.tableHeader}>取引名</th>
								<th className={styles.tableHeader}>ステージ</th>
								<th className={styles.tableHeader}>メイン担当者</th>
								<th className={styles.tableHeader}>登録日時</th>
							</tr>
						</thead>
						<tbody>
							{contracts.map((contract) => (
								<tr key={contract.id} className={styles.tableRow}>
									<td className={styles.tableCell}>{contract.id}</td>
									<td className={styles.tableCell}>{contract.name}</td>
									<td className={styles.tableCell}>
										{contract.dealstage ?? "-"}
									</td>
									<td className={styles.tableCell}>
										{contract.mainAssignee ?? "-"}
									</td>
									<td className={styles.tableCell}>
										{formatRegisterDate(contract.registerTime)}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				)}
			</div>
		</div>
	)
}

export default HubspotContractsSection
